import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { Russian } from "flatpickr/dist/l10n/ru.js"

export default class extends Controller {
  static values = {
    enableTime: { type: Boolean, default: true },
    noCalendar: { type: Boolean, default: false }
  }

  connect() {
    const isRussian = document.documentElement.lang === "ru"

    if (this.enableTimeValue) this.element.step = "300"
    this.disableKeyboard(this.element)

    this.picker = flatpickr(this.element, {
      allowInput: false,
      altInputClass: this.element.className,
      altFormat: this.altFormat(isRussian),
      altInput: true,
      clickOpens: true,
      dateFormat: this.dateFormat,
      disableMobile: true,
      enableTime: this.enableTimeValue,
      locale: isRussian ? Russian : undefined,
      minuteIncrement: this.enableTimeValue ? 5 : 1,
      noCalendar: this.noCalendarValue,
      onClose: () => this.closePicker(),
      onOpen: (_selectedDates, _dateStr, instance) => this.openPicker(instance, isRussian),
      onReady: (_selectedDates, _dateStr, instance) => this.preparePicker(instance, isRussian),
      time_24hr: true
    })

    this.disableKeyboard(this.picker.altInput)
  }

  disconnect() {
    this.hideBackdrop()
    this.picker?.destroy()
  }

  openPicker(instance, isRussian) {
    this.prepareTimeInputs(instance)
    this.showBackdrop(instance, isRussian)
  }

  closePicker() {
    this.hideBackdrop()
  }

  showBackdrop(instance, isRussian) {
    if (!this.mobileViewport) return

    this.hideBackdrop()

    this.backdrop = document.createElement("button")
    this.backdrop.type = "button"
    this.backdrop.className = "flatpickr-backdrop"
    const closeLabel = this.noCalendarValue
      ? (isRussian ? "Закрыть выбор времени" : "Close time picker")
      : (isRussian ? "Закрыть календарь" : "Close calendar")
    this.backdrop.setAttribute("aria-label", closeLabel)
    this.backdrop.addEventListener("click", () => instance.close())

    document.body.append(this.backdrop)
  }

  hideBackdrop() {
    this.backdrop?.remove()
    this.backdrop = null
  }

  addCloseButton(instance, isRussian) {
    if (!instance.calendarContainer) return
    if (instance.calendarContainer.querySelector("[data-flatpickr-close]")) return

    const actions = document.createElement("div")
    actions.className = "flatpickr-actions"

    const button = document.createElement("button")
    button.type = "button"
    button.dataset.flatpickrClose = "true"
    button.className = "flatpickr-close-button"
    button.textContent = isRussian ? "Готово" : "Done"
    button.addEventListener("click", () => instance.close())

    actions.append(button)
    instance.calendarContainer.append(actions)
  }

  preparePicker(instance, isRussian) {
    if (this.enableTimeValue) this.addTimeControls(instance, isRussian)
    this.addCloseButton(instance, isRussian)
    this.prepareTimeInputs(instance)
  }

  prepareTimeInputs(instance) {
    if (!this.enableTimeValue) return

    this.disableKeyboard(instance.hourElement)
    this.disableKeyboard(instance.minuteElement)
    this.disableKeyboard(instance.secondElement)
  }

  addTimeControls(instance, isRussian) {
    if (!instance.calendarContainer) return
    if (instance.calendarContainer.querySelector("[data-flatpickr-time-panel]")) return

    const panel = document.createElement("div")
    panel.className = "flatpickr-time-panel"
    panel.dataset.flatpickrTimePanel = "true"

    const quickTimes = document.createElement("div")
    quickTimes.className = "flatpickr-time-panel__chips"

    ;[[9, 0], [12, 0], [15, 0], [18, 0]].forEach(([hours, minutes]) => {
      const button = this.buildTimeButton(this.formatTime(hours, minutes), () => this.setTime(instance, hours, minutes))
      button.setAttribute("aria-label", isRussian ? `Установить ${button.textContent}` : `Set ${button.textContent}`)
      quickTimes.append(button)
    })

    const steppers = document.createElement("div")
    steppers.className = "flatpickr-time-panel__steppers"
    ;[
      [isRussian ? "-1 ч" : "-1 h", -60],
      [isRussian ? "+1 ч" : "+1 h", 60],
      [isRussian ? "-5 мин" : "-5 min", -5],
      [isRussian ? "+5 мин" : "+5 min", 5]
    ].forEach(([labelText, minutes]) => {
      steppers.append(this.buildTimeButton(labelText, () => this.adjustTime(instance, minutes)))
    })

    panel.append(quickTimes, steppers)
    instance.calendarContainer.append(panel)
  }

  buildTimeButton(label, onClick) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "flatpickr-time-panel__button"
    button.textContent = label
    button.addEventListener("click", onClick)

    return button
  }

  setTime(instance, hours, minutes) {
    const date = this.selectedOrCurrentDate(instance)
    date.setHours(hours, minutes, 0, 0)
    instance.setDate(date, true)
  }

  adjustTime(instance, minutes) {
    const date = this.selectedOrCurrentDate(instance)
    date.setMinutes(date.getMinutes() + minutes, 0, 0)
    instance.setDate(date, true)
  }

  selectedOrCurrentDate(instance) {
    if (instance.selectedDates[0]) return new Date(instance.selectedDates[0])

    const date = new Date()
    const minutes = Math.ceil(date.getMinutes() / 5) * 5
    date.setMinutes(minutes, 0, 0)

    return date
  }

  formatTime(hours, minutes) {
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`
  }

  altFormat(isRussian) {
    if (this.noCalendarValue) return "H:i"
    if (this.enableTimeValue) return isRussian ? "d.m.Y H:i" : "M j, Y H:i"

    return isRussian ? "d.m.Y" : "M j, Y"
  }

  get dateFormat() {
    if (this.noCalendarValue) return "H:i"

    return this.enableTimeValue ? "Y-m-d H:i" : "Y-m-d"
  }

  disableKeyboard(input) {
    if (!input) return

    input.readOnly = true
    input.setAttribute("inputmode", "none")
  }

  get mobileViewport() {
    return window.matchMedia("(max-width: 639px)").matches
  }
}
